import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { getTenantSlugFromHostname } from '../utils/tenantDetection';

/**
 * Tenant configuration interface.
 */
interface TenantConfig {
  tenant_slug: string;
  tenant_name: string;
  central_postal_code: string;
  api_base_url: string;
}

interface TenantContextType {
  tenant: TenantConfig | null;
  isLoading: boolean;
  error: string | null;
}

const TenantContext = createContext<TenantContextType>({
  tenant: null,
  isLoading: true,
  error: null,
});

/**
 * Tenant name mapping.
 */
function getTenantName(slug: string): string {
  const names: Record<string, string> = {
    'orders': 'ShareWares',
    'alphaselect': 'Alpha Select',
  };
  return names[slug] || 'ShareWares';
}

/**
 * Get tenant configuration from hostname.
 * All tenants share the same backend API.
 */
function getTenantConfig(): TenantConfig {
  const hostname = window.location.hostname;
  const tenantSlug = getTenantSlugFromHostname();

  const apiBaseUrl = process.env.REACT_APP_API_URL ||
    (hostname === 'localhost' || hostname === '127.0.0.1'
      ? 'http://localhost:8000/'
      : 'https://api.orders.sharewares.ca/');

  return {
    tenant_slug: tenantSlug,
    tenant_name: getTenantName(tenantSlug),
    central_postal_code: process.env.REACT_APP_CENTRAL_POSTAL_CODE || 'V5Y 3T8',
    api_base_url: apiBaseUrl,
  };
}

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const config = getTenantConfig();
      setTenant(config);
      setIsLoading(false);
    } catch (err) {
      console.error('Failed to load tenant config:', err);
      setError('Failed to load tenant configuration');
      setIsLoading(false);
    }
  }, []);

  const value = useMemo(
    () => ({ tenant, isLoading, error }),
    [tenant, isLoading, error]
  );

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

export default TenantContext;

